/*!
  Pioneer WFRIA ListViewUI v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["ListViewUI"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("ListViewUI") < 0) {
    window.__PIONEER_GLOBAL__.components.push("ListViewUI");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["data", "getItemKey", "children", "itemNumbers", "emptyState", "expandable", "selectable", "totalItems", "customStrings", "header", "footer", "paginator", "aria-label"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React from "react";
import Checkbox from "../Checkbox/Checkbox";
import { useUniqueId } from "../common/hooks";
import { __uniqueID } from "../common/utilities";
import EmptyState from "../EmptyState/EmptyState";
import SelectAll from "../private/SelectAll/SelectAll";
import SelectAllStrings from "../private/SelectAll/strings";
import ExpandableButton from "../Table/ExpandableRow/ExpandableButton";
import { emptyStateDefault } from "./constants";
import extractTheme from "./extractTheme";
import ListViewItem from "./ListViewItem";
import STRINGS from "./strings";
/**
 * Presentational component responsible for rendering the UI based on
 * the props passed by the render
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function ListViewUI(props) {
  var _customStrings$noResu, _customStrings$noResu2;

  const {
    data,
    getItemKey,
    children,
    itemNumbers,
    emptyState,
    expandable,
    selectable,
    totalItems,
    customStrings,
    header,
    footer,
    paginator,
    "aria-label": ariaLabel
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded);

  const theme = useTheme();
  const {
    listView,
    listviewHeader,
    listviewHeaderTitle,
    listViewBody,
    emptyFeedback,
    listViewRadioButtonHeader,
    expandAllButton,
    listViewSelectable,
    listViewItemNumberHeader,
    listViewSelectAll,
    listViewFooter
  } = extractTheme(_objectSpread({
    theme
  }, otherProps));
  const liveRegionNodeRef = /*#__PURE__*/React.createRef();
  const listViewCheckboxName = useUniqueId("listViewCheckbox-");
  let listViewId = useUniqueId("pnr-listview-");

  const emptyStateObject = _objectSpread(_objectSpread({}, emptyStateDefault), emptyState);

  const {
    message,
    actionElement,
    alignContent,
    emptyStateHeader,
    messageId,
    noIllustration
  } = emptyStateObject;
  const emptyDatatTitle = customStrings === null || customStrings === void 0 ? void 0 : (_customStrings$noResu = customStrings.noResults) === null || _customStrings$noResu === void 0 ? void 0 : _customStrings$noResu.title;
  const emptyDataBody = customStrings === null || customStrings === void 0 ? void 0 : (_customStrings$noResu2 = customStrings.noResults) === null || _customStrings$noResu2 === void 0 ? void 0 : _customStrings$noResu2.body;
  /**
   * If users passes in both the emptyState header and customStrings.noResults.title
   * emptyState header will have priority.
   */

  const emptyHeaderString = emptyStateHeader === null && emptyDatatTitle ? emptyDatatTitle : emptyStateHeader;
  /**
   * If users passes in both the emptyState message and customStrings.noResults.body,
   * emptyState message will have priority.
   */

  const emptyMessageString = (message === STRINGS.noResults || message === STRINGS.noResultsFindBy) && emptyDataBody ? emptyDataBody : message;
  /**
   * Returns the correct selected item for the "Select All" button based on
   * the selections state.
   */

  const getSelectAllSelectedOption = (allSelected, allSelectedOnPage, allIndeterminate, pageIndeterminate) => {
    // all items from the current page are selected
    if (allSelectedOnPage && allIndeterminate) {
      return SelectAllStrings.selectAllOnThisPage;
    } // current page unselected


    if (!pageIndeterminate && allIndeterminate) {
      return SelectAllStrings.unselectAllOnThisPage;
    } // some items in the current page are selected


    if (pageIndeterminate) {
      return "";
    } // all items are selected


    if (allSelected) {
      return SelectAllStrings.selectAllItems;
    } // all items unselected


    return SelectAllStrings.unselectAllItems;
  };
  /**
   * Returns the right item number, taking pagination into
   * account.
   * @param {Number} index
   */


  function getItemNumber(index) {
    if (itemNumbers && itemNumbers.offset) {
      return itemNumbers.offset + index;
    }

    if (paginator) {
      return paginator.pageOffset + index;
    }

    return 1 + index;
  }
  /**
   * Returns the right data, taking pagination into
   * account.
   */


  function getData() {
    if (paginator) {
      return paginator.paginatedData;
    }

    return data;
  }
  /**
   * Get the selectable props passed and set default values.
   */


  function getSelectableProps() {
    return _objectSpread({
      items: [],
      disabledItems: []
    }, selectable);
  }
  /**
   * Get the expandable props passed and set default values.
   */


  function getExpandableProps() {
    return _objectSpread({
      items: [],
      disabledItems: []
    }, expandable);
  }
  /**
   * Returns the accessible aria-label.
   * @param {String|Function} customString as an argument.
   * @param {Number} itemsCount The number of items.
   * @returns  {String} Returns the accessible label.
   */


  const getAriaLabelFromCustomString = (customString, itemsCount) => typeof customString === "function" ? customString(itemsCount) : customString;
  /**
   * It unselects all items in the current page.
   */


  const handleUnselectAllItemsOnPage = () => selectable.onSelectAllVisible(false);
  /**
   * It selects all items in the current page.
   */


  const handleSelectAllItemsOnPage = () => {
    selectable.onSelectAllVisible(true);
  };
  /**
   * It selects/unselects all items
   */


  const toggleSelectAllAcrossPages = select => selectable.onSelectAll(select);
  /**
   * It selects or deslects all items
   */


  const handleCheckboxSelection = _ref => {
    let {
      checked
    } = _ref;
    return selectable.onSelectAllVisible(checked);
  };
  /**
   * It renders the `Select All` control as a `checkbox` if paginator is not present,
   * or as a `menubutton` otherwise.
   */


  function renderSelectAll() {
    const {
      items: selectedItems,
      allEnabledSelected,
      allSelected,
      allSelectedOnPage,
      pageIndeterminate,
      disabledItems,
      allIndeterminate,
      selectionType
    } = selectable;

    if (selectionType === "multi") {
      const noData = getData().length === 0;

      if (paginator) {
        return _jsx(SelectAll, {
          controlMenuButtonFocus: paginator.remote,
          disabled: noData,
          selectedValue: getSelectAllSelectedOption(allSelected, allSelectedOnPage, allIndeterminate, pageIndeterminate),
          css: listViewSelectAll,
          handleDeselectAllItemsOnPage: handleUnselectAllItemsOnPage,
          handleSelectAllItemsOnPage: handleSelectAllItemsOnPage,
          handleDeselectAllAcrossPages: () => toggleSelectAllAcrossPages(false),
          handleSelectAllAcrossPages: () => toggleSelectAllAcrossPages(true),
          totalItems: totalItems - disabledItems.length,
          totalItemsSelectedAcrossAllPages: selectedItems.length,
          customStrings: customStrings
        });
      }

      const checkboxAriaLabel = getAriaLabelFromCustomString(customStrings.selectAll, selectedItems.length);
      return _jsx(Checkbox, {
        name: listViewCheckboxName,
        disabled: noData,
        css: listViewSelectable,
        "aria-label": checkboxAriaLabel,
        label: "",
        labelHidden: true,
        checked: allEnabledSelected,
        indeterminate: allIndeterminate,
        onValueChange: handleCheckboxSelection
      });
    } else {
      listViewId = `pnr-listview-${__uniqueID()}`;
      return _jsx("div", {
        css: listViewRadioButtonHeader
      });
    }
  }
  /**
   * Calls the `children` render prop, and passes the necessary props and callbacks.
   */


  const renderListViewItem = (item, index) => {
    const itemKey = getItemKey(item); // Item numbers

    const itemNumber = itemNumbers ? getItemNumber(index) : null; // Expandable

    const {
      items: expandedItems,
      disabledItems: expandedDisabledItems,
      onSelect: onExpand
    } = getExpandableProps();
    const isExpanded = expandedItems.indexOf(itemKey) !== -1;
    const isExpandableButtonDisabled = expandedDisabledItems.indexOf(itemKey) !== -1; // Selectable

    const {
      items: selectedItems,
      disabledItems,
      onSelect,
      selectionType
    } = getSelectableProps();
    const isSelected = selectedItems.indexOf(itemKey) !== -1;
    const isSelectionControlDisabled = disabledItems.indexOf(itemKey) !== -1; // Call the render prop and pass essential data and callbacks.

    const listViewItem = children({
      item,
      itemKey,
      itemNumber,
      isExpanded,
      isSelected,
      onSelect,
      onExpand,
      isExpandableButtonDisabled,
      isSelectionControlDisabled
    }); // If the consumer returns a custom component from the render prop, don't do anything and just return it.

    if (listViewItem.type !== ListViewItem) {
      return listViewItem;
    }
    /**
     * Otherwise, clone the result of the render prop, and inject all of the props,
     * and callbacks, necessary for the `ListViewItem` to render properly.
     */


    const listViewItemProps = listViewItem.props;
    const listViewItemClone = /*#__PURE__*/React.cloneElement(listViewItem, _objectSpread(_objectSpread({}, listViewItemProps), {}, {
      item,
      key: itemKey,
      itemKey,
      listId: listViewId,
      itemNumber,
      expandable: expandable !== null,
      expanded: isExpanded,
      expandableButtonDisabled: isExpandableButtonDisabled,
      onExpand,
      selectable: selectable !== null,
      selected: isSelected,
      selectionControlDisabled: isSelectionControlDisabled,
      onSelect,
      selectionType,
      customStrings,
      liveRegionNodeRef
    }));
    return listViewItemClone;
  }; // all selectable props


  const {
    items: selectedItems
  } = getSelectableProps(); // all expandable props

  const {
    allSelected: expandedAllSelected,
    onSelectAll: onExpandAll
  } = getExpandableProps();
  const dataSet = getData();
  /**
   * An identifier for accessible label of string type.
   * Eg: Expand All 4 rows.
   */

  const {
    collapseAll,
    expandAll
  } = customStrings;
  const label = getAriaLabelFromCustomString(expandedAllSelected ? collapseAll : expandAll, totalItems);
  return (// a11y Expand/Collapse requirement.
    _jsxs("section", {
      "aria-label": ariaLabel,
      css: listView,
      children: [_jsxs("div", {
        css: listviewHeader,
        children: [_jsxs("div", {
          css: listviewHeader,
          children: [itemNumbers && _jsx("div", {
            css: listViewItemNumberHeader,
            "aria-hidden": "true",
            className: "pnr-listview__item-number-header",
            children: "#"
          }), expandable && _jsx("div", {
            css: expandAllButton,
            children: _jsx(ExpandableButton, {
              expandAll: true,
              disabled: dataSet.length === 0,
              expanded: expandedAllSelected,
              onExpandedChange: (itemKey, expanded) => onExpandAll(expanded),
              "aria-label": label,
              hiddenTextPlaceholder: totalItems,
              customStrings: customStrings,
              liveRegionNodeRef: liveRegionNodeRef
            })
          }), selectable && renderSelectAll()]
        }), _jsx("div", {
          css: listviewHeaderTitle,
          children: typeof header === "function" ? header({
            selectedItems
          }) : header
        })]
      }), dataSet.length > 0 && dataSet !== null ? _jsx("ul", {
        css: listViewBody,
        children: dataSet.map(renderListViewItem)
      }) : _jsx(EmptyState, {
        actionElement: actionElement,
        alignContent: alignContent,
        css: emptyFeedback,
        header: emptyHeaderString,
        message: emptyMessageString,
        messageId: messageId,
        noIllustration: noIllustration
      }), footer && _jsx("div", {
        css: listViewFooter,
        children: typeof footer === "function" ? footer({
          selectedItems
        }) : footer
      })]
    })
  );
}

ListViewUI.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.func.isRequired,
  getItemKey: PropTypes.func.isRequired,
  // aria-label for the ListView section.
  "aria-label": PropTypes.string,
  customStrings: PropTypes.shape({
    checkboxAriaLabelSelect: PropTypes.func,
    collapse: PropTypes.string,
    collapseAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    collapseAllRowsAnnouncement: PropTypes.func,
    collapsedRowAnnouncement: PropTypes.func,
    expand: PropTypes.string,
    expandAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    expandAllRowsAnnouncement: PropTypes.func,
    expandedRowAnnouncement: PropTypes.func,
    item: PropTypes.string,
    itemsSelected: PropTypes.string,
    noResults: PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string
    }),
    rowExpandButtonLabel: PropTypes.func,
    select: PropTypes.string,
    selectAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    selectAllItems: PropTypes.string,
    selectAllOnThisPage: PropTypes.string,
    selectAllButton: PropTypes.string,
    unselectAllItems: PropTypes.string,
    unselectAllOnThisPage: PropTypes.string
  }),
  data: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types
  emptyState: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  expandable: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  footer: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  header: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  itemNumbers: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    offset: PropTypes.number
  })]),
  paginator: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  selectable: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  totalItems: PropTypes.number
} : {};
ListViewUI.defaultProps = {
  "aria-label": null,
  customStrings: {},
  data: [],
  emptyState: emptyStateDefault,
  expandable: null,
  footer: null,
  header: null,
  itemNumbers: null,
  paginator: null,
  selectable: null,
  totalItems: null
};
export default ListViewUI;